import React, { useEffect } from 'react';
import { createUseStyles } from "react-jss";
import bars from "images/bars.svg";
import { PageInner } from 'components/PageContainers';
import linkIcon from 'images/link.svg';
import { useRef } from 'react';

// Component styling groups
// body text ---- p, ol, ul
// graphics ----- img, figure, pre
const useStyles = createUseStyles(theme => ({
  blogContent: {
    maxWidth: '70rem',
    margin: '0 auto',
    wordBreak: 'break-word',
    padding: '0 1rem 0 1.5rem',
    '@global': {
      'img': {
        margin: '0 auto',
        display: 'block',
        maxWidth: '100%'
      },
      '.image_resized': {
        marginLeft: 'auto',
        marginRight: 'auto'
      },
      '.image_resized img': {
        width: '100%',
      },
      'a': {
        color: theme.colors.orange.main
      },
      'a, li, p': {
        fontSize: '1.6rem'
      },
      'li, p': {
        lineHeight: '2.6rem'
      },
      '.media': {
        position: 'relative',
        overflow: 'hidden',
        height: 0,
        paddingTop: '56.25%'
      },
      'iframe': {
        position: 'absolute',
        height: '100%',
        width: '100%',
        top: 0,
        left: 0
      },
      'pre, figcaption': {
        fontSize: '1.4rem'
      },
      'figcaption': {
        fontStyle: 'italic',
        marginTop: '1rem',
        textAlign: 'center'
      },
      'h2, h3, h4': {
        position: 'relative',
        fontWeight: 'bold',
      },
      '.h-slug-link': {
        position: 'absolute',
        transform: 'translate(-125%, -50%)',
        top: '50%',
        opacity: 0,
        [`@media(max-width: ${theme.breakpoints.small})`]: {
          opacity: 1
        }
      },
      'h2:hover .h-slug-link, h3:hover .h-slug-link, h4:hover .h-slug-link, .h-slug-link:focus': {
        opacity: 1
      },
      '.h-slug-text': {
        clip: 'rect(0 0 0 0)',
        clipPath: 'inset(50%)',
        height: '1px',
        overflow: 'hidden',
        position: 'absolute',
        whiteSpace: 'nowrap',
        width: '1px'
      },
      '.h-slug-icon': {
        content: `url(${linkIcon})`,
        display: 'block',
        width: '20px',
        [`@media(max-width: ${theme.breakpoints.small})`]: {
          width: '15px'
        }
      },
      'blockquote': {
        margin: 0,
        padding: '20px',
        borderLeft: `5px solid ${theme.colors.teal.main}`,
        backgroundColor: theme.colors.gray.lightest,
        [`@media(min-width: 450px)`]: {
          padding: '20px 110px 20px 20px',
          backgroundImage: `url(${bars})`,
          backgroundPosition: 'right top 35px',
          backgroundSize: '105px',
          backgroundRepeat: 'no-repeat',
          backgroundColor: theme.colors.gray.lightest,
          backgroundPosition: 'right top',
        }
      },
      'p code, pre': {
        borderRadius: '5px',
        background: theme.colors.gray.lightest,
        border: `1px solid ${theme.colors.gray.light}`
      },
      'p code': {
        padding: '.25rem .6rem',
        fontFamily: 'Consolas,Monaco,Andale Mono,Ubuntu Mono,monospace',
        color: theme.colors.red,
        fontSize: '1.4rem'
      },
      'hr': {
        border: 0,
        background: '#c2c9cb',
        height: '1px',
        margin: 0
      },
      'h4': {
        margin: '2rem 0 1rem',
        fontSize: '1.6rem',
        lineHeight: '2.4rem'
      },
      'h3': {
        fontSize: '2.5rem'
      },
      'h2, h3, figure': {
        margin: 0
      },
      'li + li': {
        marginTop: '.8rem'
      },
      'h2 + h3': {
        marginTop: '3.1rem'
      },
      'h2 + h2, blockquote + h2, img + h2, hr + h2, figure + h2, pre + h2, img + img, figure + figure, pre + figure, figure + pre, hr + img, blockquote + h3, p + blockquote , ul + blockquote, ol + blockquote, blockquote + p, blockquote + ul, blockquote + ol, img + pre, pre + img': {
        marginTop: '5.4rem'
      },
      'h3 + h2, h2 + img, h2 + figure, h2 + pre, h3 + img, h3 + figure, h3 + pre, p + img, ol + img, ul + img, p + figure, ol + figure, ul + figure, p + pre, ol + pre, ul + pre': {
        marginTop: '4.4rem'
      },
      'p + h2, ol + h2, ul + h2': {
        marginTop: '5.6rem'
      },
      'h3 + h3, img + h3, figure + h3, pre + h3, hr + h3': {
        marginTop: '3.9rem'
      },
      'p + h3, ol + h3, ul + h3': {
        marginTop: '3.0rem'
      },
      'h2 + p, h2 + ul, h2 + ol': {
        marginTop: '.8rem'
      },
      'h3 + p, h3 + ul, h3 + ol': {
        marginTop: '.6rem'
      },
      'p + p, ul + p, ol + p, p + ul, ul + ul, ol + ul, p + ol, ul + ol, ol + ol, h2 + blockquote, h3 + blockquote': {
        marginTop: '2.9rem'
      },
      'img + p, img + ul, img + ol, figure + p, figure + ul, figure + ol, pre + p, pre + ul, pre + ol, hr + p, hr + ul, hr + ol': {
        marginTop: '3.8rem'
      },
      'blockquote + blockquote': {
        marginTop: '2.4rem'
      },
      'img + blockquote, figure + blockquote, pre + blockquote, hr + blockquote': {
        marginTop: '6.4rem'
      },
      'blockquote + img, blockquote + figure, blockquote + pre': {
        marginTop: '6.8rem'
      },
      'hr + h2, hr + h3, hr + blockquote, hr + img, hr + figure, hr + pre': {
        marginTop: '3.2rem'
      },
      'h2 + hr, h3 + hr, p + hr, ul + hr, ol + hr, blockquote + hr, img + hr, figure + hr, pre + hr': {
        marginTop: '4.2rem'
      },
      '.blog-toc-header': {
        display: 'flex',
        flexDirection: 'row',
        marginBottom: '1.0rem'
      },
      '.blog-toc': {
        borderLeft: '8px solid #DDD',
        margin: '0 0 20px 0',
        padding: '15px',
        backgroundColor: 'rgb(245, 245, 245)'
      },
      '.blog-toc h2': {
        fontSize: '1.6rem',
        marginTop: '0.4rem'
      },
      '.blog-toc ul': {
        // paddingLeft: '2.1rem',
        paddingLeft: '1.9rem',
        marginBottom: '0px'
      },
      '.blog-toc li': {
        // paddingLeft: '0.8rem',
        marginTop: '0px'
      },
      '.blog-toc a': {
        color: 'black',
        fontSize: '1.4rem',
        lineHeight: '2.4rem'
      },
      '.blog-toc-icon': {
        width: '1.9rem',
        height: '1.9rem',
        margin: '0 1rem 0 0',
        display: 'none'
        //display: 'inline-block'
      },
      // '.blog-toc .h-slug-icon': {
      //     opacity: '0'
      // }
    }
  }
}));

const BlogContent = ({ content }) => {
  const styles = useStyles();
  const blogContent = useRef(null);

  function handleHeadingHash() {
    if (!location.hash || document.querySelector(":target")) return;

    const heading = document.getElementById(`user-content-${location.hash.slice(1)}`);
    window.scrollTo(0, heading.getBoundingClientRect().y + window.scrollY);
  }

  useEffect(() => {
    handleHeadingHash();
    window.addEventListener('hashchange', handleHeadingHash);

    return () => {
      window.removeEventListener('hashchange', handleHeadingHash);
    }
  }, []);

  return (
    <PageInner>
      <div className={styles.blogContent} ref={blogContent} dangerouslySetInnerHTML={{ __html: content }} />
    </PageInner>
  )
}

export default BlogContent;